import { GenericAbortSignal } from "axios";
import { MprcAutoCompleteHierarchical } from "components/MprcAutoCompleteHierarchical";
import { AutoCompleteHierarchicalModel, AutoCompleteHierarchicalSuggestions } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { UserSelectionEntityType, UserSelectionEntry } from "generated/CoreApi_v1";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    label?: string;
    placeholder?: string;
    selectedItems: AutoCompleteHierarchicalModel[];
    multiSelect: boolean;
    autoFocus?: boolean;
    forSurvey: boolean;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    useHistory?: boolean;
    saveHistory?: boolean;
    exceptQuestionGroupIds?: string[];

    onChange: (item: (AutoCompleteHierarchicalModel)[]) => void;
    onError: (error: any) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete hierarquico de grupos de questões.
 * @param props
 */
export const QuestionGroupAutoCompleteHierarchical: React.FC<IProps> = (props) => {

    const {
        required = false,
        disabled = false,
        useHistory = true,
        saveHistory = true,
        exceptQuestionGroupIds = [],
        label, placeholder, multiSelect, autoFocus, forSurvey, selectedItems, error, helperText, onChange, onError
    } = props;

    const api = useCoreApi();
    const session = useSession();
    const [saveSelection, setSaveSelection] = useState<AutoCompleteHierarchicalModel[] | undefined>(undefined);

    /// Carrega a lista de sugestões
    const getSuggestions = (parentId: string | null, trySelectionHistory: boolean, searchText: string, pageNumber: number, pageSize: number, signal: GenericAbortSignal | undefined): Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>> => {
        searchText = searchText.toLowerCase();

        return new Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>>((resolve) => {
            api.v1.lmsSuggestions.questionGroupHierarchical({
                parentId: parentId,
                trySelectionHistory: trySelectionHistory && useHistory === true,
                searchText: searchText,
                exceptQuestionGroupIds: exceptQuestionGroupIds,
                pageNumber: pageNumber,
                pageSize: pageSize,
                forSurvey
            }, { signal })
                .then(result => {
                    resolve({
                        items: result.data.items as AutoCompleteHierarchicalModel[],
                        totalRows: result.data.totalRows
                    });
                })
                .catch(error => {
                    if (!isCanceledError(error)) {
                        resolve({ items: [], totalRows: 0 });
                        onError(error);
                    }
                });
        });
    };

    const handleChange = (items: AutoCompleteHierarchicalModel[]) => {
        if (saveHistory === true) setSaveSelection(items as AutoCompleteHierarchicalModel[]);
        onChange(items);
    };

    const handleDisplayLabel = (option: AutoCompleteHierarchicalModel): string => {
        return option.label;
    };

    /// Salva itens selecionados.
    useEffect(() => {
        if (saveSelection != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.map((v) => ({
                    entityType: UserSelectionEntityType.QuesionGroup,
                    entityId: v.key,
                    otherInfo: 'false'
                } as UserSelectionEntry))
            };

            api.v1.user.saveSelectionHistory(payload)
                .then(() => {
                    setSaveSelection(undefined);
                })
                .catch((e) => {
                    setSaveSelection(undefined);
                });
        }
    }, [saveSelection]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcAutoCompleteHierarchical
                label={label ?? ""}
                placeholder={placeholder ?? ""}
                multiSelect={multiSelect}
                disabled={disabled}
                selectedMode="free"
                selectedItems={selectedItems}
                autoFocus={autoFocus}
                onGetSuggestions={getSuggestions}
                onChange={handleChange}
                onDisplayLabel={(option) => handleDisplayLabel(option)}
                error={error}
                helperText={helperText}
                required={required}
            />
        </>
    );
};
